import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './PricingPage.css';

interface PlanFeature {
  text: string;
}

interface Plan {
  tier: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  features: PlanFeature[];
  popular?: boolean;
}

const PLANS: Plan[] = [
  {
    tier: 'starter',
    name: 'Starter',
    monthlyPrice: 49,
    annualPrice: 490,
    features: [
      { text: 'Review monitoring (Google + Yelp)' },
      { text: 'AI-powered review responses' },
      { text: 'Social media scheduling' },
      { text: 'Analytics and reporting' },
      { text: 'QR code menus' },
      { text: 'Promotions and discount codes' },
    ],
  },
  {
    tier: 'pro',
    name: 'Pro',
    monthlyPrice: 99,
    annualPrice: 990,
    features: [
      { text: 'Review monitoring (Google + Yelp)' },
      { text: 'AI-powered review responses' },
      { text: 'Social media scheduling' },
      { text: 'Analytics and reporting' },
      { text: 'QR code menus' },
      { text: 'Promotions and discount codes' },
      { text: 'Waitlist with SMS notifications' },
    ],
    popular: true,
  },
];

function getAnnualSavingsPercent(monthly: number, annual: number): number {
  const fullYearMonthly = monthly * 12;
  const savings = ((fullYearMonthly - annual) / fullYearMonthly) * 100;
  return Math.round(savings);
}

const PricingPage: React.FC = () => {
  const { isAuthenticated, session, getIdToken } = useAuth();
  const navigate = useNavigate();

  const [billingInterval, setBillingInterval] = useState<'monthly' | 'annual'>('monthly');
  const [promoCode, setPromoCode] = useState('');
  const [loadingTier, setLoadingTier] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [promoApplied, setPromoApplied] = useState(false);

  const handleApplyPromo = () => {
    if (!promoCode.trim()) {
      setError('Please enter a promo code.');
      return;
    }
    setPromoApplied(true);
    setError(null);
  };

  const handleGetStarted = async (plan: Plan) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setLoadingTier(plan.tier);
    setError(null);

    try {
      const token = await getIdToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        setLoadingTier(null);
        return;
      }

      const payload = session?.getIdToken()?.payload;
      const email = payload?.email || '';
      const enterpriseId = payload?.['custom:enterprise_id'] || payload?.enterpriseId || 'new';

      const response = await fetch(API_ENDPOINTS.billingCheckout(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          planTier: plan.tier,
          billingInterval,
          storeId: enterpriseId,
          email,
          ...(promoApplied && promoCode.trim() ? { promoCode: promoCode.trim() } : {}),
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Checkout failed (${response.status})`);
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL returned from server.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create checkout session. Please try again.');
    } finally {
      setLoadingTier(null);
    }
  };

  return (
    <div className="pricing-page">
      <div className="pricing-header">
        <h1 className="pricing-title">Choose Your Plan</h1>
        <p className="pricing-subtitle">
          Scale your restaurant operations with the right plan for your business.
        </p>
      </div>

      {error && (
        <div className="pricing-error-banner">
          <span>{error}</span>
          <button
            className="pricing-error-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            X
          </button>
        </div>
      )}

      <div className="billing-toggle">
        <span
          className={`toggle-label ${billingInterval === 'monthly' ? 'active' : ''}`}
        >
          Monthly
        </span>
        <button
          className={`toggle-switch ${billingInterval === 'annual' ? 'toggled' : ''}`}
          onClick={() =>
            setBillingInterval(billingInterval === 'monthly' ? 'annual' : 'monthly')
          }
          aria-label="Toggle billing interval"
        >
          <span className="toggle-knob" />
        </button>
        <span
          className={`toggle-label ${billingInterval === 'annual' ? 'active' : ''}`}
        >
          Annual
        </span>
      </div>

      <div className="pricing-grid">
        {PLANS.map((plan) => {
          const price = billingInterval === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
          const savingsPercent = getAnnualSavingsPercent(plan.monthlyPrice, plan.annualPrice);
          const isLoading = loadingTier === plan.tier;

          return (
            <div
              key={plan.tier}
              className={`plan-card ${plan.popular ? 'popular' : ''}`}
            >
              {plan.popular && (
                <div className="popular-badge">Most Popular</div>
              )}
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price-wrapper">
                <span className="plan-price">${price}</span>
                <span className="plan-interval">
                  /{billingInterval === 'monthly' ? 'mo' : 'yr'}
                </span>
              </div>
              {billingInterval === 'annual' && savingsPercent > 0 && (
                <div className="plan-savings">Save {savingsPercent}%</div>
              )}
              <ul className="plan-features">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="plan-feature-item">
                    {feature.text}
                  </li>
                ))}
              </ul>
              <button
                className="plan-cta"
                disabled={isLoading}
                onClick={() => handleGetStarted(plan)}
              >
                {isLoading ? 'Processing...' : 'Get Started'}
              </button>
            </div>
          );
        })}
      </div>

      <div className="promo-section">
        <p className="promo-label">Have a promo code?</p>
        <div className="promo-input-group">
          <input
            type="text"
            className="promo-input"
            placeholder="Enter promo code"
            value={promoCode}
            onChange={(e) => {
              setPromoCode(e.target.value);
              if (promoApplied) setPromoApplied(false);
            }}
          />
          <button className="promo-apply-btn" onClick={handleApplyPromo}>
            {promoApplied ? 'Applied' : 'Apply'}
          </button>
        </div>
        {promoApplied && (
          <p className="promo-success">Promo code will be applied at checkout.</p>
        )}
      </div>
    </div>
  );
};

export default PricingPage;
